import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueueDequeue } from '../contexts/QueueDequeueContext';
import { useQueueDispatch } from '../contexts/QueueDispatchContext';
import {
  agentLanguageCode,
  callEntry,
  formatEta,
  fullName,
  partyMarker,
  sameLanguage,
  setDeparturePlayLanguage,
  uncallEntry,
} from '../services/queue';
import LanguageFlag from './LanguageFlag';
import QueuePickerModal from './QueuePickerModal';
import type { ConfigurationRelease, QueueEntry, StartedEntry } from '../types/database';
import type { ToastData } from './Toast';

// "Volgende bezoeker" — the queue-first dispatch pop-up. Opens automatically
// when a PC becomes free while visitors wait (QueueDispatchContext trigger),
// or manually from the NextUpWidget. Flow: visitor (queue selection: override
// else head; or a PINNED started visitor being moved from another PC) →
// experience (booked picks first, the room's other releases after a divider)
// → language (preselected from the departure's play language, persisted when
// changed/unconfirmed) → free PC (auto-pick, manual override) → one Start
// button = launch through the tile + dequeue (with the PC id).

interface Props {
  /** Dashboard's per-PC installed-release map (room config ∩ pc_sync_status). */
  pcExperiencesMap: Map<string, ConfigurationRelease[]>;
  onToast: (t: ToastData) => void;
  /** Multiplayer / voice-chat releases go through the MultiplayerSessionModal;
   *  the queue entry ids are dequeued once that session is active. */
  onStartMultiplayer?: (
    release: ConfigurationRelease,
    pcId: string,
    dequeueEntryIds: string[],
  ) => void;
}

type DispatchEntry = QueueEntry | StartedEntry;

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('nl-NL', {
    timeZone: 'Europe/Amsterdam',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function operatorIdFromSession(): string | null {
  try {
    const raw = sessionStorage.getItem('current_operator');
    return raw ? (JSON.parse(raw).id as string) : null;
  } catch {
    return null;
  }
}

/** Natural sort so PC-2 < PC-10. */
function byPcName(a: { pcName: string }, b: { pcName: string }): number {
  return a.pcName.localeCompare(b.pcName, undefined, { numeric: true });
}

export default function DispatchModal({ pcExperiencesMap, onToast, onStartMultiplayer }: Props) {
  const queue = useQueueDequeue();
  const dispatch = useQueueDispatch();

  const pinned = dispatch.pinnedVisitor;
  // Pinned started visitor (move) else the queue selection (override else head).
  const entry: DispatchEntry | null = pinned ? pinned.entry : queue.selectedEntry;
  const open = dispatch.dispatchOpen;

  // --- Experience resolution -------------------------------------------------
  // Distinct experience ids on the booking (the per-person model doesn't map a
  // person to one pick, so ambiguity = the operator decides).
  const bookedPicks = useMemo(() => {
    const picks = entry?.experiences ?? [];
    const seen = new Set<string>();
    return picks.filter((p) => {
      if (seen.has(p.experience_id)) return false;
      seen.add(p.experience_id);
      return true;
    });
  }, [entry]);

  // All launchable releases anywhere in the room, keyed by experience_id — the
  // pool the booked experience is matched against (room config pins versions).
  const roomReleases = useMemo(() => {
    const byExp = new Map<string, ConfigurationRelease>();
    for (const releases of pcExperiencesMap.values()) {
      for (const r of releases) {
        if (!byExp.has(r.experience_id)) byExp.set(r.experience_id, r);
      }
    }
    return byExp;
  }, [pcExperiencesMap]);

  // The room's releases that are NOT on the booking, after the divider.
  const otherReleases = useMemo(() => {
    const booked = new Set(bookedPicks.map((p) => p.experience_id));
    return [...roomReleases.values()]
      .filter((r) => !booked.has(r.experience_id))
      .sort((a, b) => (a.experiences?.name ?? '').localeCompare(b.experiences?.name ?? ''));
  }, [roomReleases, bookedPicks]);

  const [pickedExperienceId, setPickedExperienceId] = useState<string | null>(null);
  const [pickedLanguage, setPickedLanguage] = useState<string | null>(null);
  const [pickedPcId, setPickedPcId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Auto-select the experience: a pinned move keeps what was running; an
  // unambiguous booking → its experience; else wait for the operator.
  const autoExperienceId =
    pinned?.experienceId && roomReleases.has(pinned.experienceId)
      ? pinned.experienceId
      : bookedPicks.length === 1 && roomReleases.has(bookedPicks[0].experience_id)
        ? bookedPicks[0].experience_id
        : null;
  const effectiveExperienceId = pickedExperienceId ?? autoExperienceId;
  const release = effectiveExperienceId ? roomReleases.get(effectiveExperienceId) ?? null : null;
  const deviatesFromBooking =
    !!effectiveExperienceId &&
    bookedPicks.length > 0 &&
    !bookedPicks.some((p) => p.experience_id === effectiveExperienceId);

  const isMultiplayer = !!(release?.experiences?.is_multiplayer || release?.experiences?.use_vc);
  const supportedLanguages = release?.experiences?.supported_languages ?? [];
  const languageCodes = supportedLanguages
    .map((sl) => sl.language?.short_name ?? null)
    .filter((c): c is string => !!c);

  // Preselect: the language that was running (move) else the departure's play
  // language, when the release supports it. Codes compare case-insensitively
  // (DB lowercase, public.languages UPPERCASE).
  const preferredCode = pinned?.language ?? agentLanguageCode(entry?.play_language);
  const prefillLanguage =
    languageCodes.find((c) => sameLanguage(c, preferredCode)) ?? null;
  const autoLanguage = languageCodes.length === 1 ? languageCodes[0] : prefillLanguage;
  const effectiveLanguage = pickedLanguage ?? autoLanguage;
  const needsLanguage = languageCodes.length > 1 && !effectiveLanguage;

  // --- Free-PC table ----------------------------------------------------------
  const freePcs = useMemo(() => {
    const rows: {
      pcId: string;
      pcName: string;
      canLaunch: boolean;
      hasExperience: boolean;
    }[] = [];
    for (const [pcId, r] of dispatch.pcReports) {
      // `recovering`: the experience died but the previous visitor is still on
      // this PC (relaunch in flight, or the agent gave up and nobody has
      // decided what happens to them yet). It reads as free, but the next
      // visitor must not be sent there.
      if (!r.online || r.running || r.recovering) continue;
      rows.push({
        pcId,
        pcName: r.pcName,
        canLaunch: r.canLaunch,
        hasExperience: effectiveExperienceId
          ? (pcExperiencesMap.get(pcId) ?? []).some(
              (rel) => rel.experience_id === effectiveExperienceId,
            )
          : true,
      });
    }
    return rows.sort(byPcName);
  }, [dispatch.pcReports, pcExperiencesMap, effectiveExperienceId]);

  // How many PCs each network is already carrying. Headsets stream over WiFi,
  // so five visitors on one router and none on the other costs picture quality
  // even though both rooms have free PCs (Dirk 2026-09-10). A PC counts as
  // load while it is running OR recovering.
  const loadPerNetwork = useMemo(() => {
    const load = new Map<string, number>();
    for (const [pcId, r] of dispatch.pcReports) {
      if (!r.online || !(r.running || r.recovering)) continue;
      const net = dispatch.networkOf(pcId) ?? 'unknown';
      load.set(net, (load.get(net) ?? 0) + 1);
    }
    return load;
  }, [dispatch.pcReports, dispatch.networkOf]);

  const networkLoadOf = useCallback(
    (pcId: string) => loadPerNetwork.get(dispatch.networkOf(pcId) ?? 'unknown') ?? 0,
    [loadPerNetwork, dispatch.networkOf],
  );

  // Auto-pick: the least loaded router first, then the just-freed PC, then name
  // order. Spreading outranks the freed PC on purpose: the PC that just came
  // free sits on the router that was busiest a second ago.
  const autoPcId = useMemo(() => {
    let eligible = freePcs.filter((p) => p.canLaunch && p.hasExperience);
    if (eligible.length === 0) return null;
    // A move must not offer the PC we just stopped, unless it is the only one.
    if (pinned && eligible.some((p) => p.pcId !== pinned.fromPcId)) {
      eligible = eligible.filter((p) => p.pcId !== pinned.fromPcId);
    }
    const lightest = Math.min(...eligible.map((p) => networkLoadOf(p.pcId)));
    const spread = eligible.filter((p) => networkLoadOf(p.pcId) === lightest);
    if (dispatch.preferredPcId && spread.some((p) => p.pcId === dispatch.preferredPcId)) {
      return dispatch.preferredPcId;
    }
    return spread[0].pcId;
  }, [freePcs, dispatch.preferredPcId, pinned, networkLoadOf]);
  const effectivePcId = pickedPcId ?? autoPcId;
  const effectivePc = freePcs.find((p) => p.pcId === effectivePcId) ?? null;

  // Reset local picks whenever the pop-up (re)opens or the visitor changes.
  const entryId = entry?.queue_entry_id ?? null;

  // Calling the visitor forward happens HERE, not at launch: they have to walk
  // up before the operator can check their experience and language with them.
  // The cleanup takes the call back when the pop-up closes or moves to someone
  // else; a launched row leaves the queue anyway and uncall tolerates that.
  // A pinned move is a visitor who is already playing, so it never calls.
  useEffect(() => {
    if (!open || pinned || !entryId) return;
    const operatorId = operatorIdFromSession();
    if (!operatorId) return;
    void callEntry(entryId, operatorId).catch((err) => {
      console.error('call forward failed', err);
    });
    return () => {
      void uncallEntry(entryId, operatorId).catch(() => {
        /* best effort: the call expires on its own */
      });
    };
  }, [open, pinned, entryId]);
  useEffect(() => {
    setPickedExperienceId(null);
    setPickedLanguage(null);
    setPickedPcId(null);
    setStarting(false);
    setPickerOpen(false);
  }, [open, entryId]);

  if (!open) return null;

  const canStart =
    !!entry && !!release && !!effectivePc && effectivePc.canLaunch &&
    !isMultiplayer && !needsLanguage && !starting;
  const canStartMultiplayer =
    !!entry && !!release && isMultiplayer && !!effectivePc && effectivePc.canLaunch &&
    !!onStartMultiplayer && !starting;

  /** Records the language the visitor actually gets on the departure when it
   *  differs from the prefill or was never confirmed. Best effort: a failure
   *  must not block the launch. */
  async function persistLanguage(e: DispatchEntry, language: string | null) {
    if (!language) return;
    if (e.play_language_confirmed && sameLanguage(language, e.play_language)) return;
    const operatorId = operatorIdFromSession();
    if (!operatorId) return;
    try {
      await setDeparturePlayLanguage(e.departure_id, language, operatorId);
    } catch (err) {
      console.error('setDeparturePlayLanguage failed', err);
      onToast({ kind: 'error', message: 'Taal niet opgeslagen op de boeking (start gaat door).' });
    }
  }

  async function start() {
    if (!entry || !release || !effectivePc) return;
    setStarting(true);
    await persistLanguage(entry, effectiveLanguage);
    const ok = await dispatch.launchOn(
      { pcId: effectivePc.pcId, pcName: effectivePc.pcName },
      release,
      effectiveLanguage,
      {
        queueEntryId: entry.queue_entry_id,
        departureId: entry.departure_id,
        customerName: fullName(entry),
      },
    );
    setStarting(false);
    if (ok) {
      if (pinned) dispatch.setPendingMove(null);
      queue.refresh();
      onToast({
        kind: 'success',
        message: pinned
          ? `${fullName(entry)} verplaatst naar ${effectivePc.pcName}`
          : `${fullName(entry)} gestart op ${effectivePc.pcName}`,
      });
      dispatch.closeDispatch();
    } else {
      onToast({
        kind: 'error',
        message: `Starten op ${effectivePc.pcName} mislukt. Kies eventueel een andere PC.`,
      });
    }
  }

  async function startMultiplayer() {
    if (!entry || !release || !effectivePc || !onStartMultiplayer) return;
    setStarting(true);
    await persistLanguage(entry, effectiveLanguage);
    setStarting(false);
    if (pinned) dispatch.setPendingMove(null);
    onStartMultiplayer(release, effectivePc.pcId, [entry.queue_entry_id]);
    dispatch.closeDispatch();
  }

  const eta = entry && 'eta_minutes' in entry ? entry.eta_minutes : null;
  const marker = entry ? partyMarker(entry) : '';

  return (
    <div className="queue-picker-overlay" onClick={dispatch.closeDispatch}>
      <div className="queue-picker-modal dispatch-modal" onClick={(e) => e.stopPropagation()}>
        <header className="queue-picker-header">
          <h2>{pinned ? 'Verplaats bezoeker' : 'Volgende bezoeker'}</h2>
          <button className="queue-picker-close" onClick={dispatch.closeDispatch} aria-label="Sluiten">
            ✕
          </button>
        </header>

        {!entry ? (
          <div className="queue-picker-empty">De wachtrij is leeg.</div>
        ) : (
          <div className="dispatch-body">
            <div className="dispatch-visitor">
              <span className="dispatch-visitor-name">
                <LanguageFlag code={entry.play_language} className="dispatch-visitor-flag" />
                {fullName(entry)}
                {marker ? <span className="dispatch-visitor-party">{marker}</span> : null}
                {'parked' in entry && entry.parked ? (
                  <span className="queue-parked-pill">even weg</span>
                ) : null}
              </span>
              <span className="dispatch-visitor-meta">
                {pinned
                  ? `gestopt op ${pinned.fromPcName}`
                  : `geboekt ${formatTime(entry.departure_start)}`}
                {!pinned && eta !== null && eta > 0 ? ` · nog ${formatEta(eta)}` : ''}
                {entry.experience_names ? ` · ${entry.experience_names}` : ''}
              </span>
              {!pinned ? (
                <button
                  type="button"
                  className="next-up-edit-link dispatch-other-link"
                  onClick={() => setPickerOpen(true)}
                >
                  Andere bezoeker…
                </button>
              ) : null}
            </div>

            {/* Experience: booked picks first, the room's other releases after a divider */}
            <div className="dispatch-section">
              <span className="dispatch-section-label">
                {bookedPicks.length > 1 ? 'Welke ervaring?' : 'Ervaring'}
              </span>
              <div className="dispatch-chips">
                {bookedPicks.map((p) => (
                  <button
                    key={p.experience_id}
                    className={`dispatch-chip dispatch-chip--booked ${effectiveExperienceId === p.experience_id ? 'selected' : ''}`}
                    onClick={() => { setPickedExperienceId(p.experience_id); setPickedLanguage(null); setPickedPcId(null); }}
                    disabled={!roomReleases.has(p.experience_id)}
                    title={roomReleases.has(p.experience_id) ? 'Geboekte ervaring' : 'Niet beschikbaar in deze ruimte'}
                  >
                    {p.name}
                    <span className="dispatch-chip-tag">geboekt</span>
                  </button>
                ))}
                {bookedPicks.length > 0 && otherReleases.length > 0 ? (
                  <span className="dispatch-chips-divider" aria-hidden="true" />
                ) : null}
                {otherReleases.map((r) => (
                  <button
                    key={r.experience_id}
                    className={`dispatch-chip ${effectiveExperienceId === r.experience_id ? 'selected' : ''}`}
                    onClick={() => { setPickedExperienceId(r.experience_id); setPickedLanguage(null); setPickedPcId(null); }}
                  >
                    {r.experiences?.name ?? 'Onbekend'}
                  </button>
                ))}
              </div>
              {bookedPicks.length === 0 && !pinned ? (
                <span className="dispatch-warning">
                  Geen geboekte ervaring gevonden. Kies handmatig.
                </span>
              ) : null}
              {deviatesFromBooking ? (
                <span className="dispatch-hint">
                  Wijkt af van boeking ({bookedPicks.map((p) => p.name).join(' + ')})
                </span>
              ) : null}
            </div>

            {/* Language chips whenever the release supports 2+ languages */}
            {languageCodes.length > 1 && (
              <div className="dispatch-section">
                <span className="dispatch-section-label">Taal</span>
                <div className="dispatch-chips">
                  {languageCodes.map((code) => (
                    <button
                      key={code}
                      className={`dispatch-chip dispatch-chip--lang ${sameLanguage(effectiveLanguage, code) ? 'selected' : ''}`}
                      onClick={() => setPickedLanguage(code)}
                    >
                      <LanguageFlag code={code} className="dispatch-chip-flag" />
                      {code.toUpperCase()}
                    </button>
                  ))}
                </div>
                {!entry.play_language_confirmed ? (
                  <span className="dispatch-hint">Taal nog niet bevestigd bij check-in</span>
                ) : null}
              </div>
            )}

            {/* Free PCs */}
            <div className="dispatch-section">
              <span className="dispatch-section-label">Vrije PC</span>
              {freePcs.length === 0 ? (
                <span className="dispatch-warning">Geen vrije PC's op dit moment.</span>
              ) : (
                <div className="dispatch-pc-grid">
                  {freePcs.map((p) => (
                    <button
                      key={p.pcId}
                      className={`dispatch-pc ${effectivePcId === p.pcId ? 'selected' : ''} ${!p.canLaunch ? 'blocked' : ''}`}
                      onClick={() => setPickedPcId(p.pcId)}
                      disabled={!p.canLaunch}
                      title={
                        !p.canLaunch
                          ? 'PC-checks niet in orde (headset/diagnose/sync)'
                          : !p.hasExperience
                            ? 'Ervaring niet geïnstalleerd op deze PC'
                            : pinned && p.pcId === pinned.fromPcId
                              ? 'Dezelfde PC als waar de bezoeker gestopt is'
                              : undefined
                      }
                    >
                      {p.pcName}
                      {!p.hasExperience && p.canLaunch && <span className="dispatch-pc-flag">!</span>}
                    </button>
                  ))}
                </div>
              )}
              {effectivePc && !effectivePc.hasExperience && (
                <span className="dispatch-warning">
                  Ervaring niet geïnstalleerd op {effectivePc.pcName}. Starten kan mislukken.
                </span>
              )}
            </div>

            {isMultiplayer && (
              <div className="dispatch-section">
                <span className="dispatch-hint">
                  Multiplayer-ervaring: de sessie wordt via het multiplayer-venster gestart.
                  {' '}De bezoeker verlaat de wachtrij zodra de sessie loopt.
                </span>
                <button
                  className="queue-picker-primary dispatch-mp-btn"
                  onClick={() => void startMultiplayer()}
                  disabled={!canStartMultiplayer}
                  title={!onStartMultiplayer ? 'Niet beschikbaar op dit scherm' : undefined}
                >
                  Start multiplayer{effectivePc ? ` (${effectivePc.pcName})` : ''}
                </button>
              </div>
            )}
          </div>
        )}

        <footer className="queue-picker-footer">
          <button className="queue-picker-secondary" onClick={dispatch.closeDispatch}>
            Later
          </button>
          <button className="queue-picker-primary" onClick={() => void start()} disabled={!canStart}>
            {starting
              ? 'Starten…'
              : effectivePc
                ? pinned
                  ? `Verplaats naar ${effectivePc.pcName}`
                  : `Start op ${effectivePc.pcName}`
                : 'Start'}
          </button>
        </footer>
      </div>

      {pickerOpen ? <QueuePickerModal onClose={() => setPickerOpen(false)} /> : null}
    </div>
  );
}
